import React, { FunctionComponent, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { loadPieChartData } from 'app/Requests';
import styles from './PieChart.module.scss';
import TimeIntervalFilter from 'app/components/TimeIntervalFilter';
import NoData from 'app/components/NoData';
import CardTitle from 'app/components/CardTitle';
import Loader from 'app/components/Loader';
import { useQuery } from 'react-query';
import { handleTranslatedChartData, isItArabicText } from 'app/utils/helpers';
import ServerError from 'app/components/ErrorBoundary/ServerError';
import { useAppSettings } from 'app/context/settings/appSettings';
import { getFromLocalStorage } from 'app/utils/localStorage';

interface Prop {
  data?: { series: number[]; labels: string[] };
  slug?: string;
  ids?: number[];
  compId: string;
  pieTitle?: string;
  translateData?: boolean;
  hideFilter?: boolean;
  additionalComponents?: boolean;
  isReport?: boolean;
  isEnForTest?: boolean;
}

const PieChart: FunctionComponent<Prop> = ({
  ids,
  slug,
  data,
  pieTitle,
  compId,
  translateData,
  additionalComponents,
  isReport,
  hideFilter,
  isEnForTest,
}) => {
  const isEn = getFromLocalStorage('LNG') == 'en' || isEnForTest ? true : false;
  const [filter, setFilter] = useState<string>('');
  const [preventLoading, setPreventLoading] = useState(true);
  useEffect(() => {
    setTimeout(() => {
      setPreventLoading(false);
    }, 2200);
  }, []);
  const { theme } = useAppSettings();
  const handleChangeTime = (option: string) => {
    setFilter(option);
  };

  const {
    isLoading,
    data: QData,
    isError,
  } = useQuery(
    [
      'loadPieChartData',
      {
        slug,
        body: {
          time_interval: filter,
          hashtag_ids: ids,
          limit: 5,
        },
      },
    ],
    loadPieChartData,
    { enabled: !!slug },
  );
  const noFormattedData = !!slug ? QData : data || { series: [], labels: [] };
  const chartData = translateData
    ? handleTranslatedChartData(noFormattedData)
    : noFormattedData;

  return (
    <ServerError requiredApiCalls={[isError]}>
      <div id={compId}>
        <div id={`${compId}-pieChart`} className="position-relative">
          {pieTitle && (
            <CardTitle title={pieTitle} compId={compId} hideIcon={isReport} />
          )}

          <div className={styles.PieChart} id={`${compId}-pieChartContent`}>
            <TimeIntervalFilter
              compId={compId}
              handleChangeTime={handleChangeTime}
              hideInput={hideFilter}
              isReport={isReport}
            />
            {isLoading || preventLoading ? (
              <Loader />
            ) : !chartData?.series?.length ? (
              <NoData />
            ) : (
              <div data-testid="chart-holder">
                <Chart
                  options={getPieOptions(
                    chartData?.labels,
                    theme?.colors?.chartsColors,
                    chartData,
                    isEn,
                  )}
                  series={chartData?.series}
                  type="pie"
                  height={220}
                />
              </div>
            )}
          </div>
          {additionalComponents}
        </div>
      </div>
    </ServerError>
  );
};

export default PieChart;

function getPieOptions(
  labels: string[],
  colors: string[],
  chartData: any,
  isEn: boolean,
): any {
  const options = {
    labels: labels,
    chart: {
      // width:'100px',
      id: 'pie-chart',
    },
    dataLabels: {
      enabled: true,
    },
    plotOptions: {
      pie: {
        // size: 100,
        // donut: {
        //     size: '10%',
        // },
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      width: '50px',
      fontSize: '10px',
      fontFamily: ' LatoRegular, sans-serif',
      formatter: function (seriesName: any, value: any) {
        const totalSeriesValues = chartData?.series?.reduce(function (
          a: number,
          b: number,
        ) {
          return a + b;
        }, 0);
        const index = value.seriesIndex;
        const percentage = (
          (chartData?.series?.[index] / totalSeriesValues) *
          100
        ).toFixed(1);
        return isEn
          ? isItArabicText(seriesName)
            ? ` <span>%${percentage}</span><span dir='auto'>${seriesName}</span>`
            : `<span dir='auto'>${seriesName} ${percentage}%<span>`
          : isItArabicText(seriesName)
            ? `<span dir='auto'>${seriesName} ${percentage}%</span>`
            : `<span>${percentage}%</span><span dir='auto'>${seriesName}</span>`;
      },
    },
    colors: colors,
    tooltip: {
      fillSeriesColor: false,
    },
  };
  return options;
}
